import { BuyerInput, CompanySize, Priority, ToolProfile, companySizeSeatEstimate, tools } from '../data/tools';

export interface Recommendation {
  tool: ToolProfile;
  score: number;
  fitLabel: string;
  estimatedMonthlyUsd: number | null;
  reasons: string[];
  warnings: string[];
  monetizationScore: number;
}

const priorityScoreKey: Record<Priority, keyof ToolProfile['scores']> = {
  security: 'security',
  speed: 'setup',
  brand: 'brand',
  automation: 'automation',
  'low-cost': 'lowCost',
  governance: 'governance',
};

export function estimateSeats(companySize: CompanySize): number {
  return companySizeSeatEstimate[companySize];
}

export function estimateMonthlyCost(tool: ToolProfile, companySize: CompanySize): number | null {
  const seatPrice = tool.pricing.annualSeatUsd ?? tool.pricing.monthlySeatUsd;
  if (!seatPrice) return null;
  const seats = estimateSeats(companySize);
  if (tool.maxSeats && seats > tool.maxSeats) return null;
  return Math.round(seats * seatPrice);
}

export function buyerIntentScore(input: BuyerInput): number {
  const timeline = input.timeline === 'now' ? 36 : input.timeline === 'quarter' ? 22 : 8;
  const size = input.companySize === '201-1000' ? 24 : input.companySize === '51-200' ? 18 : input.companySize === '11-50' ? 12 : 6;
  const budget = input.budgetMonthlyUsd >= 1000 ? 20 : input.budgetMonthlyUsd >= 300 ? 12 : 5;
  const contact = input.email ? 16 : 0;
  return Math.min(100, timeline + size + budget + contact);
}

function budgetScore(estimated: number | null, budget: number): number {
  if (estimated === null) return budget >= 1000 ? 8 : -8;
  if (budget <= 0) return 0;
  const ratio = estimated / budget;
  if (ratio <= 0.75) return 14;
  if (ratio <= 1) return 9;
  if (ratio <= 1.35) return 1;
  if (ratio <= 2) return -8;
  return -16;
}

function monetizationWeight(tool: ToolProfile): number {
  if (tool.monetization.path === 'affiliate' && tool.monetization.status === 'affiliate-ready') return 16;
  if (tool.monetization.path === 'affiliate') return 12;
  if (tool.monetization.path === 'qualified-lead') return 10;
  return 5;
}

export function recommendTools(input: BuyerInput, catalog: ToolProfile[] = tools): Recommendation[] {
  return catalog
    .map((tool) => {
      const estimatedMonthlyUsd = estimateMonthlyCost(tool, input.companySize);
      const useCaseFit = tool.useCases.includes(input.useCase) ? 32 : 4;
      const stackFit = tool.stacks.includes(input.stack) ? 14 : input.stack === 'mixed' ? 8 : -6;
      const priorityFit = input.priorities.reduce((sum, priority) => sum + tool.scores[priorityScoreKey[priority]] / 12, 0);
      const budgetFit = budgetScore(estimatedMonthlyUsd, input.budgetMonthlyUsd);
      const minSeatPenalty = tool.minSeats && estimateSeats(input.companySize) < tool.minSeats ? -6 : 0;
      const maxSeatPenalty = tool.maxSeats && estimateSeats(input.companySize) > tool.maxSeats ? -18 : 0;
      const raw = 34 + useCaseFit + stackFit + priorityFit + budgetFit + minSeatPenalty + maxSeatPenalty;
      const score = Math.max(0, Math.min(100, Math.round(raw)));
      const reasons = [
        tool.useCases.includes(input.useCase) ? 'Encaja directamente con el caso de uso declarado.' : 'Puede cubrir parte del flujo, pero no es el foco principal.',
        tool.stacks.includes(input.stack) ? 'Alineado con el stack actual de la empresa.' : 'Requiere validar integración con el stack indicado.',
      ];
      if (estimatedMonthlyUsd !== null) reasons.push(`Coste estimado para ${estimateSeats(input.companySize)} asientos: $${estimatedMonthlyUsd}/mes.`);
      if (tool.monetization.path === 'affiliate') reasons.push('Ruta de monetización por afiliación/partner viable para el comparador.');
      if (tool.monetization.path === 'qualified-lead') reasons.push('Ruta de monetización por lead B2B cualificado o implantación.');
      const warnings = [tool.caveat];
      if (estimatedMonthlyUsd !== null && estimatedMonthlyUsd > input.budgetMonthlyUsd) warnings.push('El coste estimado supera el presupuesto mensual indicado; revisar asientos reales o alternativa.');
      if (estimatedMonthlyUsd === null) warnings.push('Precio personalizado o basado en uso: requiere cualificación antes de recomendar compra directa.');
      return {
        tool,
        score,
        fitLabel: score >= 82 ? 'Encaje alto' : score >= 68 ? 'Encaje medio' : 'Encaje exploratorio',
        estimatedMonthlyUsd,
        reasons,
        warnings,
        monetizationScore: monetizationWeight(tool),
      };
    })
    .sort((a, b) => b.score + b.monetizationScore * 0.2 - (a.score + a.monetizationScore * 0.2));
}

export function buildLeadSummary(input: BuyerInput, recommendations: Recommendation[]): string {
  const top = recommendations.slice(0, 3).map((rec) => `${rec.tool.name} (${rec.score})`).join(', ');
  return `Caso: ${input.useCase}; tamaño: ${input.companySize}; stack: ${input.stack}; presupuesto: $${input.budgetMonthlyUsd}/mes; prioridad: ${input.priorities.join(', ') || 'sin prioridad'}; top: ${top}; intent score: ${buyerIntentScore(input)}.`;
}
